import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { AuthUser, AuthTenant } from '@/types';
import { supabase } from '@/lib/supabase';

export interface PortalAccount {
  /**
   * Cuándo eligió su propia contraseña. `null` significa que sigue usando la
   * de fábrica —su número de teléfono— y hay que insistirle.
   */
  passwordChangedAt?: string | null;
  id: string;
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tenantId: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  tenantWhatsappNumber: string | null;
  tenantSupportEmail: string | null;
}

interface AuthState {
  session: Session | null;
  /** Admin / staff user (null for pet parents). */
  user: AuthUser | null;
  /** Current tenant (admin context only). */
  tenant: AuthTenant | null;
  /** Pet parent portal account (null for staff). */
  portal: PortalAccount | null;
  hydrated: boolean;
  setSessionData: (s: Session | null) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logout: () => Promise<void>;
}

async function loadStaffProfile(
  session: Session,
): Promise<{ user: AuthUser; tenant: AuthTenant | null } | null> {
  const [profileRes, rolesRes] = await Promise.all([
    supabase
      .from('user_profiles')
      .select(
        'id,tenant_id,branch_id,email,first_name,last_name,avatar_url,status,tenants(id,slug,name,plan,status,license_until,trial_ends_at,currency,locale,timezone,logo_url,brand_color,tax_enabled,tax_rate,tax_label,whatsapp_number,support_email,show_service_duration,appointment_slot_min,daily_slots,saturday_slots,legal_name,nit,address,phone,municipality,neighborhood)',
      )
      .eq('id', session.user.id)
      .maybeSingle(),
    supabase
      .from('user_roles')
      .select('roles(key,permissions)')
      .eq('user_id', session.user.id),
  ]);

  if (profileRes.error || !profileRes.data) return null;

  const p = profileRes.data as unknown as {
    id: string; tenant_id: string | null; branch_id: string | null;
    email: string; first_name: string; last_name: string;
    avatar_url: string | null; status: AuthUser['status'];
    tenants: {
      id: string; slug: string; name: string; plan: string;
      status: string | null;
      license_until: string | null; trial_ends_at: string | null;
      currency: string; locale: string; timezone: string;
      logo_url: string | null; brand_color: string | null;
      tax_enabled: boolean; tax_rate: number; tax_label: string;
      show_service_duration: boolean | null;
      appointment_slot_min: number | null;
      daily_slots: number | null; saturday_slots: number | null;
      whatsapp_number: string | null; support_email: string | null;
      legal_name: string | null; nit: string | null; address: string | null;
      phone: string | null; municipality: string | null; neighborhood: string | null;
    } | null;
  };

  const perms = new Set<string>();
  const roles: string[] = [];
  type RoleRef = { key: string; permissions: string[] };
  for (const row of (rolesRes.data ?? []) as unknown as Array<{ roles: RoleRef | RoleRef[] | null }>) {
    const r = Array.isArray(row.roles) ? row.roles[0] : row.roles;
    if (!r) continue;
    roles.push(r.key);
    for (const perm of r.permissions) perms.add(perm);
  }

  const isSuperAdmin = roles.includes('SUPERADMIN');

  const user: AuthUser = {
    id: p.id, tenantId: p.tenant_id, email: p.email,
    firstName: p.first_name, lastName: p.last_name,
    avatarUrl: p.avatar_url, status: p.status, branchId: p.branch_id,
    roles, permissions: [...perms],
    isSuperAdmin,
  };
  const tenant: AuthTenant | null = p.tenants ? {
    id: p.tenants.id, slug: p.tenants.slug, name: p.tenants.name,
    plan: p.tenants.plan, currency: p.tenants.currency,
    // Hacen falta para avisar del vencimiento antes de que ocurra: sin
    // ellos la pantalla no puede decir hasta cuándo hay acceso.
    status: p.tenants.status ?? null,
    licenseUntil: p.tenants.license_until,
    trialEndsAt: p.tenants.trial_ends_at,
    locale: p.tenants.locale, timezone: p.tenants.timezone,
    logoUrl: p.tenants.logo_url, brandColor: p.tenants.brand_color,
    taxEnabled: p.tenants.tax_enabled ?? true,
    // Por defecto se muestran: una empresa que no configuró nada espera ver
    // los campos, no que desaparezcan sin explicación.
    showServiceDuration: p.tenants.show_service_duration ?? true,
    appointmentSlotMin: p.tenants.appointment_slot_min ?? 60,
    dailySlots: p.tenants.daily_slots ?? 7,
    saturdaySlots: p.tenants.saturday_slots ?? 10,
    taxRate: Number(p.tenants.tax_rate ?? 0.19),
    taxLabel: p.tenants.tax_label ?? 'IVA',
    whatsappNumber: p.tenants.whatsapp_number,
    supportEmail: p.tenants.support_email,
    legalName: p.tenants.legal_name,
    nit: p.tenants.nit,
    address: p.tenants.address,
    phone: p.tenants.phone,
    municipality: p.tenants.municipality,
    neighborhood: p.tenants.neighborhood,
  } : null;
  return { user, tenant };
}

/** Pet parent portal — a customer with a linked auth user. */
async function loadPortalAccount(session: Session): Promise<PortalAccount | null> {
  const { data, error } = await supabase
    .from('customer_portal_accounts')
    .select(`
      id, customer_id, tenant_id, password_changed_at,
      customer:customers(first_name, last_name, email, phone),
      tenant:tenants(name, logo_url, whatsapp_number, support_email)
    `)
    .eq('auth_user_id', session.user.id)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as unknown as {
    id: string; customer_id: string; tenant_id: string;
    password_changed_at: string | null;
    customer: { first_name: string; last_name: string; email: string | null; phone: string } | null;
    tenant: {
      name: string; logo_url: string | null;
      whatsapp_number: string | null; support_email: string | null;
      legal_name: string | null; nit: string | null; address: string | null;
      phone: string | null; municipality: string | null; neighborhood: string | null;
    } | null;
  };
  // If RLS stripped the joins (shouldn't happen now that portal policies are
  // in place, but guard anyway) bail out cleanly instead of crashing.
  if (!row.customer || !row.tenant) return null;
  return {
    id: row.id,
    customerId: row.customer_id,
    tenantId: row.tenant_id,
    firstName: row.customer.first_name,
    lastName: row.customer.last_name,
    email: row.customer.email ?? session.user.email ?? '',
    phone: row.customer.phone,
    tenantName: row.tenant.name,
    tenantLogoUrl: row.tenant.logo_url,
    tenantWhatsappNumber: row.tenant.whatsapp_number,
    tenantSupportEmail: row.tenant.support_email,
    // NULL = sigue entrando con su teléfono como contraseña.
    passwordChangedAt: row.password_changed_at,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  tenant: null,
  portal: null,
  hydrated: false,

  async setSessionData(session) {
    if (!session) {
      set({ session: null, user: null, tenant: null, portal: null, hydrated: true });
      return;
    }
    // Try staff first (most common); if none, try portal.
    const staff = await loadStaffProfile(session);
    if (staff) {
      // Block staff login if their tenant is suspended/expired (super admins
      // skip this check — they manage the platform itself).
      if (!staff.user.isSuperAdmin) {
        const { data: check } = await supabase.rpc('tenant_access_check');
        const allowed = (check as { allowed?: boolean } | null)?.allowed ?? true;
        const reason = (check as { reason?: string; message?: string } | null);
        if (!allowed) {
          await supabase.auth.signOut();
          set({ session: null, user: null, tenant: null, portal: null, hydrated: true });
          // Defer toast import to avoid circular deps; emit a custom event
          // the login page picks up.
          const detail = { message: reason?.message ?? 'Acceso bloqueado', reason: reason?.reason };
          window.dispatchEvent(new CustomEvent('groomingpro:tenant-access-blocked', { detail }));
          return;
        }
      }
      set({ session, user: staff.user, tenant: staff.tenant, portal: null, hydrated: true });
      return;
    }
    const portal = await loadPortalAccount(session);
    set({ session, user: null, tenant: null, portal, hydrated: true });
  },

  async refreshProfile() {
    const s = get().session;
    if (!s) return;
    const staff = await loadStaffProfile(s);
    if (staff) { set({ user: staff.user, tenant: staff.tenant, portal: null }); return; }
    const portal = await loadPortalAccount(s);
    set({ user: null, tenant: null, portal });
  },

  async logout() {
    await supabase.auth.signOut();
    set({ session: null, user: null, tenant: null, portal: null });
  },
}));

// Bootstrap: load existing session on page load, then subscribe to changes.
void supabase.auth.getSession().then(({ data }) => {
  void useAuthStore.getState().setSessionData(data.session);
});

supabase.auth.onAuthStateChange((_event, session) => {
  void useAuthStore.getState().setSessionData(session);
});
